import React, { useEffect, useState } from "react";
import PreviewWaveform from "../components/waveform";
import {
  PausePreview,
  PlayIcon,
  PlayPreview,
  Stars,
} from "../components/Icons";
import Head from "next/head";
import { getSharedTrackData } from "../utils/apis";
import { useRouter } from "next/router";
import useRandomPattern from "../utils/randomPattern";
import * as gtag from "../utils/gtag";
import { handleLoggedIn } from "../utils/serverProps";

export async function getServerSideProps(context) {
  return handleLoggedIn(context);
}

const Share = (props) => {
  const router = useRouter();
  const [playingPreview, setPlayingPreview] = useState(-1);
  const [shareData, setShareData] = useState<any>({});
  const { id, pattern: oldPattern } = router.query;
  const [seekPosition, setSeekPosition] = useState(0);

  const { color, randomIcon } = useRandomPattern(id, oldPattern, "");

  const togglePreview = (index) => {
    if (playingPreview == index) {
      setPlayingPreview(-1);
    }
    if (playingPreview == -1 || playingPreview !== index) {
      setPlayingPreview(index);
    }
  };

  useEffect(() => {
    const getShareData = async () => {
      const res = await getSharedTrackData(id);
      setShareData(res);
      gtag.event(
        {
          action: "view_share_page",
          category: "share-page",
        },
        {
          shareId: id,
          category: "share-page",
          creator: res?.email,
          user: props.session ? props.session.user.email : null,
          loggedIn: props.session && props.session.user.email ? true : false,
        }
      );
    };
    getShareData();
  }, [id]);

  const handleSeek = (event, position) => {
    event.stopPropagation();
    setSeekPosition(position);
  };

  return (
    <div className="w-full h-full">
      <Head>
        <title>{shareData?.title} | Beatoven.ai</title>
        <link rel="icon" href="/favicon.ico" />
        <meta
          property="og:image"
          content="https://cdn.prod.website-files.com/65d772a7cbf713010f7736f6/67069ba14456f823d9d11717_website%20preview%20image.webp"
        />
        <meta
          property="og:url"
          content={`https://${process.env.NEXT_PUBLIC_SITE_URL}/share?id=${id}&pattern=${oldPattern}`}
        />
      </Head>
      <div
        className="overflow-x-hidden w-screen h-screen text-white"
        style={{
          background: `linear-gradient(to bottom, black, ${color})`,
        }}
      >
        <div className="flex gap-2 items-center justify-center w-full pt-4 pb-3 top-0 z-10">
          <img className="w-auto h-8 pl-4 mt-0.5 sm:pl-0 " src="/banner.png" />
        </div>
        <div className="flex items-center justify-center w-full relative max-w-4xl mx-auto top-32 px-1 sm:top-10">
          <div className="flex items-start justify-between w-full gap-10 sm:flex-col">
            <div className="w-1/3 sm:w-full">
              <div
                className={
                  "rounded-lg min-h-64 h-full min-w-40 flex items-center justify-center"
                }
                style={{
                  backgroundColor: color,
                }}
              >
                <svg
                  width="160"
                  height="160"
                  viewBox="0 0 100 100 "
                  fill="#fff"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g dangerouslySetInnerHTML={{ __html: randomIcon }} />
                </svg>
              </div>
            </div>
            <div className="flex flex-col items-start relative w-2/3 sm:w-full">
              <div className="mb-2 flex items-center gap-2 ">
                <div className="rounded-full bg-white p-1 text-center w-8 h-8 text-black uppercase">
                  {shareData?.email?.split("@")[0][0]}
                </div>
                <div className="font-poppins font-semibold text-sm text-white opacity-80 text-center capitalize">
                  {shareData?.email?.split("@")[0]}
                </div>
              </div>
              <p className="font-poppins font-medium text-3xl text-white capitalize">
                {shareData.title || "Untitled"}
              </p>
              <p className="font-mulish font-normal text-sm text-white opacity-60 mt-2">
                {shareData?.prompt?.[0] || "Untitled"}
              </p>
              <div
                className={`w-full min-h-10 h-auto px-2 mt-2 rounded-xl bg-gradient-to-r from-white/10 via-white/10 to-white/10 `}
              >
                <div className="flex items-center gap-2 w-full relative z-20">
                  <div
                    className={`w-[24px] h-[24px] my-3 sm:mr-1 hover:opacity-80`}
                    onClick={() => {
                      togglePreview(shareData.option);
                    }}
                    key={`${shareData.option}`}
                  >
                    {shareData.option === playingPreview ? (
                      <PausePreview width={24} height={24} />
                    ) : (
                      <PlayPreview width={24} height={24} />
                    )}
                  </div>
                  <div className={`w-full rounded-xl overflow-hidden relative`}>
                    {shareData && shareData.track_url && (
                      <div className="w-full" id="preview-tracks">
                        <PreviewWaveform
                          trackUrl={shareData.track_url}
                          isPlaying={shareData.option === playingPreview}
                          standalone={true}
                          showIcons={false}
                          hideLoader={true}
                          index={shareData.option}
                          isPreviewer={true}
                          isPreviewerScreen={true}
                          selectedTrack={true}
                          onSeek={handleSeek}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2 ">
                <a
                  type="button"
                  href={process.env.NEXT_PUBLIC_SITE_URL}
                  target="_blank"
                  className="w-36 font-poppins flex items-center justify-center rounded-xl text-sm px-2 py-2 text-center mr-2 mt-2 bg-green opacity-90 text-black font-semibold hover:opacity-100 outline-none"
                >
                  <Stars fill="#000" />
                  Create Music
                </a>
                <div className="absolute right-0 flex items-center gap-2 mr-2 ">
                  <PlayIcon />
                  <p className="font-mulish text-sm text-white opacity-60">
                    {shareData?.views} plays
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Share;
